/* ************************************************************
src/components/Loading.jsx
고차 컴포넌트(HOC)
지연 로딩(lazy), ErrorBoundary와 Suspense를 하나로 결합
**************************************************************/
import React, { lazy, Suspense, ComponentType, ReactNode } from "react";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";

/**
 * 옵션 객체에 대한 인터페이스 정의
 */
interface LoadingOptions {
  // error 시 커스텀 렌더링 함수를 받거나 ReactNode를 받을 수 있게 설정 가능합니다.
  error?: (props: FallbackProps) => ReactNode;
  loading?: ReactNode;
}

/**
 * 컴포넌트를 동적으로 로드하고 에러/로딩 상태를 자동으로 처리하는 헬퍼 함수입니다.
 * @param importFn - () => import('...') 형태의 함수
 * @param options - error 및 loading 컴포넌트 옵션
 */
export function Loading<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  options: LoadingOptions = {}
) {
  // lazy는 () => Promise<{ default: T }> 형태를 직접 받습니다.
  const Component = lazy(importFn);

  return function DynamicComponent(props: React.ComponentProps<T>) {
    return (
      <ErrorBoundary
        fallbackRender={
          options.error ||
          (({ error }: FallbackProps) => (
            <div>Error: {(error as Error)?.message || "Unknown error!!"}</div>
          ))
        }
      >
        <Suspense fallback={options.loading || <div>Loading...</div>}>
          {/* 전달받은 props를 컴포넌트에 그대로 넘겨줍니다. */}
          <Component {...props} />
        </Suspense>
      </ErrorBoundary>
    );
  };
}